import type { MyChronLapSummary, MyChronMapPoint } from '@kangaroo/shared';

import { speedColorBrakeToFlatOut } from './telemetrySession';

export type MapPreviewSvgSegment = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
};

export function previewSpeedRange(points: MyChronMapPoint[]) {
  if (points.length === 0) {
    return null;
  }

  const speeds = points.map((point) => point.speedKph ?? 0).sort((left, right) => left - right);
  const lowIndex = Math.max(0, Math.floor(speeds.length * 0.06));
  const highIndex = Math.min(speeds.length - 1, Math.ceil(speeds.length * 0.94) - 1);
  const min = speeds[lowIndex]!;
  const max = speeds[highIndex]!;

  if (max - min < 4) {
    return { min: Math.max(0, min - 2), max: max + 2 };
  }

  return { min, max };
}

function interpolateMapPoint(
  left: MyChronMapPoint,
  right: MyChronMapPoint,
  fraction: number,
): MyChronMapPoint {
  const leftSpeed = left.speedKph ?? 0;
  const rightSpeed = right.speedKph ?? 0;
  return {
    timeMs: left.timeMs + (right.timeMs - left.timeMs) * fraction,
    lat: left.lat + (right.lat - left.lat) * fraction,
    lon: left.lon + (right.lon - left.lon) * fraction,
    speedKph: leftSpeed + (rightSpeed - leftSpeed) * fraction,
  };
}

export function smoothClosedMapPreview(points: MyChronMapPoint[]) {
  if (points.length < 4 || points.length > 65) {
    return points;
  }
  const first = points[0]!;
  const last = points.at(-1)!;
  const closed =
    Math.abs(first.lat - last.lat) < 1e-9
    && Math.abs(first.lon - last.lon) < 1e-9;
  if (!closed) {
    return points;
  }

  let ring = points.slice(0, -1);
  const iterations = ring.length < 20 ? 3 : 2;
  for (let iteration = 0; iteration < iterations; iteration += 1) {
    const smoothed: MyChronMapPoint[] = [];
    for (let index = 0; index < ring.length; index += 1) {
      const point = ring[index]!;
      const next = ring[(index + 1) % ring.length]!;
      smoothed.push(
        interpolateMapPoint(point, next, 0.25),
        interpolateMapPoint(point, next, 0.75),
      );
    }
    ring = smoothed;
  }
  return [...ring, { ...ring[0]! }];
}

export function buildMapPreviewSvgSegments(points: MyChronMapPoint[]): MapPreviewSvgSegment[] {
  if (points.length < 2) {
    return [];
  }

  const renderedPoints = smoothClosedMapPreview(points);
  const lats = renderedPoints.map((point) => point.lat);
  const lons = renderedPoints.map((point) => point.lon);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);
  const centerLatRad = (((minLat + maxLat) / 2) * Math.PI) / 180;
  const latSpan = maxLat - minLat;
  const lonSpan = (maxLon - minLon) * Math.cos(centerLatRad);
  const maxSpan = Math.max(latSpan, lonSpan, 0.0001);
  const contentSize = 88;
  const scale = contentSize / maxSpan;
  const renderedWidth = lonSpan * scale;
  const renderedHeight = latSpan * scale;
  const offsetX = (100 - renderedWidth) / 2;
  const offsetY = (100 - renderedHeight) / 2;
  const speedRange = previewSpeedRange(renderedPoints);
  if (!speedRange) {
    return [];
  }

  return renderedPoints.slice(0, -1).map((point, index) => {
    const next = renderedPoints[index + 1]!;
    const speed = ((point.speedKph ?? 0) + (next.speedKph ?? 0)) / 2;
    return {
      x1: offsetX + (point.lon - minLon) * Math.cos(centerLatRad) * scale,
      y1: offsetY + (maxLat - point.lat) * scale,
      x2: offsetX + (next.lon - minLon) * Math.cos(centerLatRad) * scale,
      y2: offsetY + (maxLat - next.lat) * scale,
      color: speedColorBrakeToFlatOut(speed, speedRange.min, speedRange.max),
    };
  });
}

function isRacingLapSummary(lap: MyChronLapSummary) {
  return (
    lap.durationMs !== null
    && lap.durationMs >= 15_000
    && lap.durationMs <= 120_000
    && lap.endTimeMs !== null
    && (lap.segment === null || lap.segment === 0)
  );
}

function selectPreviewLap(laps: MyChronLapSummary[]) {
  const racingLaps = laps.filter(isRacingLapSummary).sort((left, right) => left.lapNumber - right.lapNumber);
  if (racingLaps.length === 0) {
    return null;
  }

  const candidateLaps = racingLaps.length > 1 ? racingLaps.slice(1) : racingLaps;
  const middleIndex = Math.floor((candidateLaps.length - 1) / 2);
  return candidateLaps[middleIndex] ?? candidateLaps[0] ?? null;
}

function lapSummaryWindow(lap: MyChronLapSummary) {
  return {
    startMs: lap.endTimeMs! - lap.durationMs!,
    endMs: lap.endTimeMs!,
  };
}

export function buildSessionLapMapPreviewPoints(
  mapPreview: MyChronMapPoint[],
  laps: MyChronLapSummary[],
): MyChronMapPoint[] {
  if (mapPreview.length < 2) {
    return mapPreview;
  }

  const previewLap = selectPreviewLap(laps);
  if (!previewLap) {
    return mapPreview;
  }

  const { startMs, endMs } = lapSummaryWindow(previewLap);
  const lapPoints = mapPreview.filter((point) => point.timeMs >= startMs && point.timeMs <= endMs);

  return lapPoints.length >= 2 ? lapPoints : mapPreview;
}
